import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { normalizePayerStatus, type PayerNormalizedStatus } from '@/lib/normalization/statusNormalization';

type PayerStatusHistoryRow = Database['public']['Tables']['payer_status_history']['Row'];

export interface StatusHistoryEntry {
  id: string;
  rawStatus: string | null;
  normalizedStatus: PayerNormalizedStatus;
  source: string;
  recordedAt: Date;
  recordedBy: string | null;
  notes: string | null;
}

interface UsePayerStatusHistoryOptions {
  authorizationId: string | null;
}

export function usePayerStatusHistory({ authorizationId }: UsePayerStatusHistoryOptions) {
  const [history, setHistory] = useState<StatusHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!authorizationId) {
      setHistory([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('payer_status_history')
        .select('*')
        .eq('authorization_id', authorizationId)
        .order('recorded_at', { ascending: false });

      if (fetchError) throw fetchError;

      const entries: StatusHistoryEntry[] = (data || []).map((row: PayerStatusHistoryRow) => ({
        id: row.id,
        rawStatus: row.raw_status,
        normalizedStatus: row.normalized_status as PayerNormalizedStatus,
        source: row.source,
        recordedAt: new Date(row.recorded_at),
        recordedBy: row.recorded_by,
        notes: row.notes,
      }));

      setHistory(entries);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [authorizationId]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const addStatusEntry = async (
    rawStatus: string,
    source: 'portal' | 'fhir' | 'fax' | 'phone' | 'manual',
    notes?: string
  ) => {
    if (!authorizationId) return;

    const { data: { user } } = await supabase.auth.getUser();
    const normalizedStatus = normalizePayerStatus(rawStatus);

    if (!normalizedStatus) return;

    const { error: insertError } = await supabase
      .from('payer_status_history')
      .insert({
        authorization_id: authorizationId,
        raw_status: rawStatus,
        normalized_status: normalizedStatus,
        source,
        recorded_by: user?.id || null,
        notes,
      });

    if (insertError) throw insertError;

    // Also update the authorization_requests table
    await supabase
      .from('authorization_requests')
      .update({
        payer_raw_status: rawStatus,
        payer_normalized_status: normalizedStatus,
      })
      .eq('id', authorizationId);

    await fetchHistory();
  };

  return {
    history,
    loading,
    error,
    refetch: fetchHistory,
    addStatusEntry,
  };
}
